import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useEcho } from "@/lib/Echo/EchoProvider";
import { useOrgId } from "@/lib/GraphQLCodegen/fetcher";
import { invalidateThreadQueries } from "@/app/Threads/utils/invalidateThreadQueries";

/**
 * Refetches thread state whenever the organisation's threads move, and after a
 * dropped socket reconnects, since anything said while it was down was missed.
 */
export function useThreadActivitySync() {
  const echo = useEcho();
  const orgId = useOrgId();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!echo || !orgId) {
      return;
    }

    function refetch() {
      invalidateThreadQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: ["ThreadMessages.Infinite"] });
    }

    const channelName = `organisation.${orgId}.threads`;
    echo.private(channelName).listen(".thread.activity", refetch);

    let wasDisconnected = false;
    const stopWatching = echo.connector.onConnectionChange((status) => {
      if (status === "connected" && wasDisconnected) {
        refetch();
      }
      wasDisconnected = status !== "connected";
    });

    return () => {
      stopWatching();
      echo.leave(channelName);
    };
  }, [echo, orgId, queryClient]);
}
